// hooks/useJournalSections.js
import { useState, useEffect } from 'react';
import { prepareSections } from '../utils/sectionHelpers';
import apiClient from '../../../apiClient';

/**
 * Custom hook för att hantera journalsektioner
 * Hanterar state, uppdateringar och autosave
 */
export const useJournalSections = ({
  selectedTemplate,
  streamingSections,
  generatedSections,
  isTranscribing,
  sessionId
}) => {
  const [sections, setSections] = useState([]);
  const [error, setError] = useState("");
  const [disableAutosaveUntil, setDisableAutosaveUntil] = useState(null);

  // Sparstatus för autosave-indikatorn och beforeunload-varningen
  // 'idle' = inget att spara ännu, 'unsaved' = osparade ändringar,
  // 'saving' = sparning pågår, 'saved' = allt sparat
  const [saveStatus, setSaveStatus] = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);


  // Hantera streaming och genererade sektioner
  const hasPartial = isTranscribing && streamingSections.length > 0;
  const hasFinal = !isTranscribing && generatedSections.length > 0;

  // Uppdatera sektioner när GPT-stream kommer
  useEffect(() => {
    // ===== DEBUG_JOURNAL_BUG: START useJournalSections effect - Remove after debugging =====
    console.log('🔄 useJournalSections effect triggered');
    console.log('   Has template:', !!selectedTemplate);
    console.log('   Template name:', selectedTemplate?.template_name || 'NOT SET');
    console.log('   hasPartial:', hasPartial);
    console.log('   hasFinal:', hasFinal);
    console.log('   streamingSections.length:', streamingSections.length);
    console.log('   generatedSections.length:', generatedSections.length);
    console.log('   Current sections.length:', sections.length);
    // ===== DEBUG_JOURNAL_BUG: END =====

    if (!selectedTemplate) {
      // ===== DEBUG_JOURNAL_BUG: - Remove after debugging =====
      console.log('   ⚠️  Early return: No template set');
      // ===== DEBUG_JOURNAL_BUG: END =====
      return;
    }

    const preparedSections = (secs) => {
      // ===== DEBUG_JOURNAL_BUG: - Remove after debugging =====
      console.log('   📦 prepareSections called with', secs.length, 'sections');
      // ===== DEBUG_JOURNAL_BUG: END =====
      const result = prepareSections(secs, selectedTemplate);
      // ===== DEBUG_JOURNAL_BUG: - Remove after debugging =====
      console.log('   📦 prepareSections returned', result.length, 'sections');
      result.forEach((s, idx) => {
        console.log(`      [${idx}] "${s.title}" | order: ${s.order}`);
      });
      // ===== DEBUG_JOURNAL_BUG: END =====
      return result;
    };

    if (hasFinal && generatedSections.length > 0) {
      // Slutgiltigt svar från backend - prioriteras alltid
      // ===== DEBUG_JOURNAL_BUG: - Remove after debugging =====
      console.log('   ➡️  Setting sections from generatedSections (hasFinal)');
      // ===== DEBUG_JOURNAL_BUG: END =====
      setSections(preparedSections(generatedSections));
    } else if (hasPartial) {
      // Streaming pågår - visa partiella sektioner
      // ===== DEBUG_JOURNAL_BUG: - Remove after debugging =====
      console.log('   ➡️  Setting sections from streamingSections');
      // ===== DEBUG_JOURNAL_BUG: END =====
      setSections(preparedSections(streamingSections));
    } else {
      // ===== DEBUG_JOURNAL_BUG: - Remove after debugging =====
      console.log('   ⏭️  No action taken');
      // ===== DEBUG_JOURNAL_BUG: END =====
    }
    // eslint-disable-next-line
  }, [selectedTemplate, hasPartial, hasFinal, streamingSections, generatedSections]);

  // Autosave journal
  useEffect(() => {
    if (!hasFinal || !sessionId || sections.length === 0) return;
    
    // Check if autosave is disabled
    const now = Date.now();
    if (disableAutosaveUntil && now < disableAutosaveUntil) {
      return;
    }
    
    const combined = sections
      .map((sec) => `${sec.title}\n${sec.content.trim() || "‎"}`)
      .join("\n\n");
      
    const timer = setTimeout(() => {
      // Double-check if autosave is still disabled
      const nowAtSave = Date.now();
      if (disableAutosaveUntil && nowAtSave < disableAutosaveUntil) {
        return;
      }

      setSaveStatus('saving');
      apiClient
        .patch(`/recorder/sessions/${sessionId}/update_journal`, {
          generated_journal: combined,
        })
        .then(() => {
          setSaveStatus('saved');
          setLastSavedAt(new Date());
        })
        .catch((err) => {
          console.error("Autosave-fel:", err);
          setSaveStatus('unsaved');
          setError("Autosave: kunde inte spara journal.");
        });
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [sections, hasFinal, sessionId, disableAutosaveUntil]);

  // CRUD operationer för sektioner
  const handleSectionChange = (idx, field, value) => {
    setSaveStatus('unsaved');
    setSections((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s))
    );
  };

  const handleDeleteSection = (idx) => {
    setSaveStatus('unsaved');
    setSections((prev) => prev.filter((_, i) => i !== idx));
  };

  const addSection = (title, content = "", order = 9999) => {
    setSaveStatus('unsaved');
    setSections(prev => {
      const newSections = [...prev, { title, content, order }];
      return prepareSections(newSections, selectedTemplate);
    });
  };

  // Anropas efter manuell sparning (Spara-knappen) så indikatorn stämmer
  const markSaved = () => {
    setSaveStatus('saved');
    setLastSavedAt(new Date());
  };

  const getJournalText = () => {
    return sections
      .map((s) => `${s.title}\n${s.content.trim()}`)
      .join("\n\n");
  };

  return {
    sections,
    setSections,
    error,
    setError,
    hasPartial,
    hasFinal,
    handleSectionChange,
    handleDeleteSection,
    addSection,
    getJournalText,
    setDisableAutosaveUntil,
    saveStatus,
    lastSavedAt,
    markSaved
  };
};